import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import apiService from '../../services/api_services.js';
import auth_service from '../../services/auth_service.js';
import { FaSearch } from 'react-icons/fa'; // Search icon
import SkeletonLoading from '../../components/SkeletonLoading';
import toast from 'react-hot-toast';
import { useWishlist } from '../../context/WishlistContext';
import { addToRecentlyViewed } from '../../utils/recentlyViewed';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const SearchHeader = styled.div`
  background-color: #1F509A;
  color: white;
`;

const SearchHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SearchTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem auto;
  max-width: 700px;
  width: 100%;
  padding: 0 1.5rem;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(0, 0, 122, 0.1);
  padding: 0.25rem 0.25rem 0.25rem 1.5rem;
  width: 100%;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:focus-within {
    box-shadow: 0 4px 25px rgba(0, 0, 122, 0.15);
    border-color: rgba(0, 0, 122, 0.1);
  }

  input {
    border: none;
    outline: none;
    padding: 1rem 0;
    font-size: 1.1rem;
    width: 100%;
    color: #333;
    
    &::placeholder {
      color: #94a3b8;
    }
  }
`;

const SearchButton = styled.button`
  background-color: #1F509A;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  margin-left: 0.5rem;
  
  &:hover {
    background-color: #000055;
    transform: translateY(-2px);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const ResultsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 3rem;
  width: 100%;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ResultsTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F509A;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 3rem;
    height: 0.25rem;
    background: linear-gradient(to right, #FFD700, #FFA500);
    border-radius: 0.25rem;
  }
`;

const ResultsCount = styled.span`
  background-color: #f0f4ff;
  color: #1F509A;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  
  &:hover {
    transform: translateY(-0.5rem);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, #1F509A, #000055);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover::after {
    transform: scaleX(1);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 220px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 80%, rgba(0, 0, 0, 0.05));
    z-index: 1;
    pointer-events: none;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: all 0.5s ease;
    z-index: 0;

    &:first-child {
      opacity: 1;
    }

    &:nth-child(2) {
      opacity: 0;
    }
  }

  &:hover img:first-child {
    opacity: 0;
    transform: scale(1.05);
  }

  &:hover img:nth-child(2) {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const WishlistButton = styled.button`
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    z-index: 20;
    background-color: ${props => props.isInWishlist ? '#ffe6e6' : 'white'};
    border: none;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    color: ${props => props.isInWishlist ? '#ef4444' : '#94a3b8'};
    
    &:hover {
      transform: scale(1.1);
      color: #ef4444;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
    
    svg {
      width: 1.25rem;
      height: 1.25rem;
      pointer-events: none;
    }
    
    .loading-spinner {
      position: absolute;
      width: 20px;
      height: 20px;
      border: 2px solid rgba(0, 0, 122, 0.3);
      border-radius: 50%;
      border-top-color: #1F509A;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
`;

const DiscountLabel = styled.span`
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #1F509A;
  padding: 0.35rem 0.75rem;
  border-radius: 50px;
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ProductContent = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Title = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1F509A;
  
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Description = styled.p`
  color: #64748b;
  font-size: 0.85rem;
  height: 2.4em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Price = styled.p`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1F509A;
  margin-top: auto;
  border-top: 1px solid #f1f5f9;
`;

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin: 2rem auto;
  max-width: 600px;
`;

const NoResultsIcon = styled.div`
  width: 6rem;
  height: 6rem;
  margin-bottom: 1.5rem;
  color: #1F509A;
  opacity: 0.3;
`;

const NoResultsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F509A;
  margin-bottom: 0.75rem;
`;

const NoResultsText = styled.p`
  color: #64748b;
  margin-bottom: 1.5rem;
  max-width: 400px;
`;

const SearchEngine = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const { isInWishlist, toggleWishlist } = useWishlist();

  const fetchFilteredProducts = async (query) => {
    setLoading(true);
    try {
      if(query === ''){
        // Don't fetch any products by default
        setProducts([]);
      } else {
        const data = await apiService.searchProducts(query);
        setProducts(data.data);
      }
    } catch (error) {
      console.error('Error fetching filtered products:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = auth_service.isAuthenticated();
      setIsLoggedIn(isAuth);
      
      if (isAuth) {
        try {
          const userData = auth_service.getAccessTokenData();
          if (userData && userData.id) {
            setCustomerId(userData.id);
          }
        } catch (error) {
          console.error("Error getting user data:", error);
        }
      }
    };
    
    checkAuth();
  }, []);
  

  
  // Function to toggle wishlist status
  const handleWishlistToggle = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!product || !product.pr_id) return;
    
    const productId = product.pr_id;
    setWishlistLoading(prev => ({ ...prev, [productId]: true }));
    
    try {
      const productData = {
        product_id: productId,
        ca_id: product.ca_id || 10,
        name: product.parent_product_name || product.name || product.product_name,
        price: product.pr_sprice || product.price,
        pr_image: product.pr_image,
        pr_image_json: product.pr_image_json,
        slug: product.slug
      };

      const result = await toggleWishlist(productData);
      
      if (result.success) {
        toast.success(result.message, {
          duration: 2000,
          position: 'top-center',
          style: {
            background: '#10B981',
            color: '#fff',
          }
        });
      } else {
        toast.error(result.message, {
          duration: 2000,
          position: 'top-center',
          style: {
            background: '#EF4444',
            color: '#fff',
          }
        });
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      toast.error("Error updating wishlist", {
        duration: 2000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
        }
      });
    } finally {
      setWishlistLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if(e.target.value === ''){
      // Clear products when search is empty
      setProducts([]);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchFilteredProducts(searchQuery);
    }
  };
  const handleSearchClick = (e) => {
    
      fetchFilteredProducts(searchQuery);
    
  };

 
  const handleCardClick = (product) => {
    // Add to recently viewed before navigating
    addToRecentlyViewed(product);
    
    navigate(`/product/${product.pr_id}/${product.ca_id}`, {
      state: { product },
    });
  };

  useEffect(() => {
    fetchFilteredProducts('');
  }, [location]);

  return (
    <Container>
      {/* Search Header */}
      <SearchHeader>
        <SearchHeaderContent>
          <SearchTitle>Search Products</SearchTitle>
          <p>Find the perfect eyewear for your style and needs</p>
        </SearchHeaderContent>
      </SearchHeader>

      {/* Search Bar */}
      <SearchBarWrapper>
        <SearchBar>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            placeholder="Search for frames, sunglasses, contact lenses..."
          />
          <SearchButton onClick={handleSearchClick}>
            <FaSearch />
            Search
          </SearchButton>
        </SearchBar>
      </SearchBarWrapper>

      {/* Results Section */}
      <ResultsContainer>
        {loading ? (
          <>
            <ResultsHeader>
              <ResultsTitle>Searching...</ResultsTitle>
              <ResultsCount>Loading products...</ResultsCount>
            </ResultsHeader>
            <ProductGrid>
              {[...Array(8)].map((_, index) => (
                <SkeletonLoading key={index} />
              ))}
            </ProductGrid>
          </>
        ) : searchQuery ? (
          products.length > 0 ? (
            <>
              <ResultsHeader>
                <ResultsTitle>Results for "{searchQuery}"</ResultsTitle>
                <ResultsCount>{products.length} {products.length === 1 ? 'product' : 'products'} found</ResultsCount>
              </ResultsHeader>
              <ProductGrid>
                {products.map((product) => {
                  const images = product.pr_image_json;
                  const firstImage = images[0] || 'https://via.placeholder.com/150';
                  const secondImage = images[1] || firstImage;
                  const showDiscount = product.pr_price && product.pr_price !== product.pr_sprice;
                  const discountPercent = showDiscount 
                    ? Math.round(((product.pr_sprice - product.pr_price) / product.pr_sprice) * 100)
                    : 0;

                  return (
                    <ProductCard key={product.pr_id} onClick={() => handleCardClick(product)}>
                      {/* {showDiscount && discountPercent > 0 && (
                        <DiscountLabel>
                          Sale
                        </DiscountLabel>
                      )} */}
                      <WishlistButton
                        isInWishlist={isInWishlist(product.pr_id)}
                        onClick={(e) => handleWishlistToggle(e, product)}
                        disabled={wishlistLoading[product.pr_id]}
                        className={wishlistLoading[product.pr_id] ? 'loading' : ''}
                      >
                        {wishlistLoading[product.pr_id] ? (
                          <div className="loading-spinner"></div>
                        ) : (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill={isInWishlist(product.pr_id) ? "#EF4444" : "none"}
                            viewBox="0 0 24 24"
                            stroke={isInWishlist(product.pr_id) ? "#EF4444" : "currentColor"}
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                            />
                          </svg>
                        )}
                      </WishlistButton>
                      <ImageWrapper>
                        <img src={firstImage} alt={product.pr_name || 'Product Image'} />
                        <img src={secondImage} alt={product.pr_name || 'Product Hover Image'} />
                      </ImageWrapper>
                      <ProductContent>
                        <Title>{product.pr_name}</Title>
                        <Price>
                          {showDiscount ? (
                            <div className="flex items-center gap-2">
                              <span>₹{product.pr_price}</span>
                              <span className="text-sm text-gray-400 line-through">₹{product.pr_sprice}</span>
                            </div>
                          ) : (
                            <>₹{product.pr_price}</>
                          )}
                        </Price>
                      </ProductContent>
                    </ProductCard>
                  );
                })}
              </ProductGrid>
            </>
          ) : (
            <NoResults>
              <NoResultsIcon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </NoResultsIcon>
              <NoResultsTitle>No products found</NoResultsTitle>
              <NoResultsText>
                We couldn't find any products matching "{searchQuery}". Try different keywords or browse our categories.
              </NoResultsText>
              <button 
                className="bg-[#1F509A] text-white px-6 py-3 rounded-lg hover:bg-[#000055] transition-colors duration-300 font-medium"
                onClick={() => {
                  setSearchQuery('');
                  fetchFilteredProducts('');
                }}
              >
                Clear Search
              </button>
            </NoResults>
          )
        ) : (
          <NoResults>
            <NoResultsIcon className="text-blue-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </NoResultsIcon>
            <NoResultsTitle className="text-2xl font-semibold text-gray-800">Search for products</NoResultsTitle>
            <NoResultsText className="text-gray-600 max-w-md text-center">
              Enter a product name or keyword in the search bar above to find what you're looking for.
            </NoResultsText>
          </NoResults>
        )}
      </ResultsContainer>
    </Container>
  );
};

export default SearchEngine;
